// Blue Strike MyFXBook Dashboard Page
// Questa dashboard si connette all'API di MyFXBook per mostrare i dati in tempo reale

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Target, DollarSign, Activity, Calendar, ExternalLink, RefreshCw } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { ROUTES, EXTERNAL_LINKS } from '../constants';

// Tipo per i dati MyFXBook
interface MyFXBookData {
    accountId: string;
    accountName: string;
    balance: number;
    equity: number;
    profit: number;
    profitPercentage: number;
    gain: number;
    drawdown: number;
    deposits: number;
    withdrawals: number;
    totalTrades: number;
    profitableTrades: number;
    winRate: number;
    profitFactor: number;
    averageWin: number;
    averageLoss: number;
    dailyGain: Array<{ date: string; value: number }>;
    recentTrades: Array<{
        openTime: string;
        closeTime: string;
        symbol: string;
        action: string;
        profit: number;
        pips: number;
    }>;
    lastUpdate: string;
}

export const BlueStrikeDashboardPage: React.FC = () => {
    const { t } = useLanguage();
    const [data, setData] = useState<MyFXBookData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
    const [tradeLimit, setTradeLimit] = useState<number | 'all'>('all');

    // Funzione per caricare i dati da MyFXBook
    const fetchMyFXBookData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Chiamata API reale a MyFXBook tramite Vercel Serverless Function
            const response = await fetch('/api/myfxbook/blue-strike');

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();

            // Se c'è un errore nell'API response
            if (apiData.error) {
                throw new Error(apiData.message || 'Errore API MyFXBook');
            }

            setData(apiData);
            setLastRefresh(new Date());

        } catch (err: any) {
            console.error('Error fetching MyFXBook data:', err);

            // Fallback ai dati mock in caso di errore
            console.warn('Using mock data as fallback');

            const mockData: MyFXBookData = {
                accountId: '11752602',
                accountName: 'CTC Blue Strike 5K',
                balance: 5944.32,
                equity: 5944.32,
                profit: 944.32,
                profitPercentage: 18.90,
                gain: 18.90,
                drawdown: 3.66,
                deposits: 5000,
                withdrawals: 0,
                totalTrades: 39,
                profitableTrades: 22,
                winRate: 56.4,
                profitFactor: 4.37,
                averageWin: 1321.07,
                averageLoss: -391.08,
                dailyGain: generateDailyGainData(),
                recentTrades: generateRecentTrades(),
                lastUpdate: new Date().toISOString()
            };

            setData(mockData);
            setLastRefresh(new Date());
            setError('Usando dati di esempio. Configura le credenziali per i dati reali.');
        } finally {
            setLoading(false);
        }
    };

    // Carica i dati all'avvio
    useEffect(() => {
        fetchMyFXBookData();

        // Auto-refresh ogni ora (60 minuti)
        const interval = setInterval(() => {
            fetchMyFXBookData();
        }, 60 * 60 * 1000); // 1 ora in millisecondi

        return () => clearInterval(interval);
    }, []);

    // Funzione helper per generare dati giornalieri (mock)
    function generateDailyGainData() {
        const data = [];
        const today = new Date();
        for (let i = 30; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            data.push({
                date: date.toISOString().split('T')[0],
                value: Math.random() * 2 - 0.5 + (30 - i) * 0.6 // Crescita verso +18.9%
            });
        }
        return data;
    }

    // Funzione helper per generare trade recenti (mock)
    function generateRecentTrades() {
        const symbols = ['XAUUSD', 'EURUSD', 'GBPUSD', 'USDJPY'];
        const actions = ['BUY', 'SELL'];
        const trades = [];

        for (let i = 0; i < 10; i++) {
            const closeTime = new Date();
            closeTime.setHours(closeTime.getHours() - i * 4);
            const openTime = new Date(closeTime);
            openTime.setMinutes(openTime.getMinutes() - 39); // Durata media 39 minuti

            const isWin = Math.random() < 0.564; // 56.4% win rate
            const pips = isWin ? (Math.random() * 2000 + 300) : -(Math.random() * 600 + 100);
            const profit = isWin ? (Math.random() * 60 + 20) : -(Math.random() * 40 + 5);

            trades.push({
                openTime: openTime.toISOString(),
                closeTime: closeTime.toISOString(),
                symbol: symbols[Math.floor(Math.random() * symbols.length)],
                action: actions[Math.floor(Math.random() * actions.length)],
                profit: profit,
                pips: pips
            });
        }

        return trades;
    }

    if (loading && !data) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#050508] via-[#0A0E1A] to-[#050508] text-white flex items-center justify-center">
                <div className="text-center">
                    <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-400">Caricamento dati MyFXBook...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#050508] via-[#0A0E1A] to-[#050508] text-white flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="text-red-500 text-6xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-bold mb-2">Errore di Connessione</h2>
                    <p className="text-gray-400 mb-6">{error}</p>
                    <button
                        onClick={fetchMyFXBookData}
                        className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors"
                    >
                        Riprova
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#050508] via-[#0A0E1A] to-[#050508] text-white">
            {/* Header */}
            <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <Link
                        to={ROUTES.BLUE_STRIKE}
                        className="inline-flex items-center text-gray-400 hover:text-white transition-colors mb-8 group"
                    >
                        <ArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" size={20} />
                        {t('common.back')}
                    </Link>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                        <div>
                            <h1 className="text-4xl sm:text-5xl font-black mb-4">
                                <span className="text-white">CTC BLUE STRIKE</span>
                                <span className="text-blue-500 ml-3">DASHBOARD</span>
                            </h1>
                            <p className="text-gray-400 text-lg">Monitoraggio Real-Time da MyFXBook</p>
                            <p className="text-gray-500 text-sm mt-2">
                                Ultimo aggiornamento: {new Date(lastRefresh).toLocaleString('it-IT')}
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-6 md:mt-0">
                            <button
                                onClick={fetchMyFXBookData}
                                disabled={loading}
                                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-semibold py-3 px-5 rounded-lg transition-colors disabled:opacity-50"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                Aggiorna
                            </button>
                            <a
                                href={EXTERNAL_LINKS.MYFXBOOK_BLUE_STRIKE}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 px-5 rounded-lg transition-colors"
                            >
                                MyFXBook
                                <ExternalLink className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    {/* Account Info Banner */}
                    <div className="bg-gradient-to-r from-blue-900/20 to-transparent border border-blue-500/30 rounded-2xl p-6 mb-8">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div>
                                <h3 className="text-lg font-bold text-white mb-1">{data?.accountName}</h3>
                                <p className="text-sm text-gray-400">Account ID: {data?.accountId}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm font-semibold text-green-400">LIVE</span>
                            </div>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                        <AnimatedSection delay={100}>
                            <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-2xl p-6 hover:border-green-500/40 transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <TrendingUp className="text-green-500" size={32} />
                                    <span className="text-xs text-green-400 font-semibold px-3 py-1 bg-green-500/20 rounded-full">
                                        GAIN
                                    </span>
                                </div>
                                <h3 className="text-3xl font-black text-white mb-1">
                                    +{data?.gain.toFixed(2)}%
                                </h3>
                                <p className="text-gray-400 text-sm">Guadagno Totale</p>
                            </div>
                        </AnimatedSection>

                        <AnimatedSection delay={200}>
                            <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-2xl p-6 hover:border-blue-500/40 transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <DollarSign className="text-blue-500" size={32} />
                                    <span className="text-xs text-blue-400 font-semibold px-3 py-1 bg-blue-500/20 rounded-full">
                                        BALANCE
                                    </span>
                                </div>
                                <h3 className="text-3xl font-black text-white mb-1">
                                    ${data?.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </h3>
                                <p className="text-gray-400 text-sm">Saldo Corrente</p>
                            </div>
                        </AnimatedSection>

                        <AnimatedSection delay={300}>
                            <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-2xl p-6 hover:border-orange-500/40 transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <Target className="text-orange-500" size={32} />
                                    <span className="text-xs text-orange-400 font-semibold px-3 py-1 bg-orange-500/20 rounded-full">
                                        WIN RATE
                                    </span>
                                </div>
                                <h3 className="text-3xl font-black text-white mb-1">
                                    {data?.winRate.toFixed(1)}%
                                </h3>
                                <p className="text-gray-400 text-sm">Tasso di Successo</p>
                            </div>
                        </AnimatedSection>

                        <AnimatedSection delay={400}>
                            <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-2xl p-6 hover:border-purple-500/40 transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <Activity className="text-purple-500" size={32} />
                                    <span className="text-xs text-purple-400 font-semibold px-3 py-1 bg-purple-500/20 rounded-full">
                                        PROFIT FACTOR
                                    </span>
                                </div>
                                <h3 className="text-3xl font-black text-white mb-1">
                                    {data?.profitFactor.toFixed(2)}
                                </h3>
                                <p className="text-gray-400 text-sm">Rapporto Profitto/Perdita</p>
                            </div>
                        </AnimatedSection>
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
                        {/* Equity Growth Chart */}
                        <AnimatedSection delay={500} className="lg:col-span-2">
                            <div className="bg-[#0F1419] border border-gray-800 rounded-2xl p-6">
                                <h3 className="text-xl font-bold mb-6 flex items-center">
                                    <TrendingUp className="mr-2 text-blue-500" size={24} />
                                    Crescita Equity (Ultimi 30 Giorni)
                                </h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={data?.dailyGain}>
                                        <defs>
                                            <linearGradient id="colorGain" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                                        <XAxis
                                            dataKey="date"
                                            stroke="#6b7280"
                                            tickFormatter={(value) => new Date(value).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                                        />
                                        <YAxis stroke="#6b7280" />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                                            labelStyle={{ color: '#f3f4f6' }}
                                            formatter={(value: any) => [`${value.toFixed(2)}%`, 'Gain']}
                                        />
                                        <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorGain)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </AnimatedSection>

                        {/* Statistics */}
                        <AnimatedSection delay={600}>
                            <div className="bg-[#0F1419] border border-gray-800 rounded-2xl p-6">
                                <h3 className="text-xl font-bold mb-6 flex items-center">
                                    <Activity className="mr-2 text-orange-500" size={24} />
                                    Statistiche
                                </h3>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center pb-3 border-b border-gray-800">
                                        <span className="text-sm text-gray-400">Trade Totali</span>
                                        <span className="text-lg font-bold text-white">{data?.totalTrades}</span>
                                    </div>
                                    <div className="flex justify-between items-center pb-3 border-b border-gray-800">
                                        <span className="text-sm text-gray-400">Trade Vincenti</span>
                                        <span className="text-lg font-bold text-green-400">{data?.profitableTrades}</span>
                                    </div>
                                    <div className="flex justify-between items-center pb-3 border-b border-gray-800">
                                        <span className="text-sm text-gray-400">Drawdown Max</span>
                                        <span className="text-lg font-bold text-red-400">{data?.drawdown.toFixed(2)}%</span>
                                    </div>
                                    <div className="flex justify-between items-center pb-3 border-b border-gray-800">
                                        <span className="text-sm text-gray-400">Media Vincita</span>
                                        <span className="text-lg font-bold text-green-400">+{data?.averageWin.toFixed(1)} pips</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-400">Media Perdita</span>
                                        <span className="text-lg font-bold text-red-400">{data?.averageLoss.toFixed(1)} pips</span>
                                    </div>
                                </div>
                            </div>
                        </AnimatedSection>
                    </div>

                    {/* Recent Trades Table */}
                    <AnimatedSection delay={700}>
                        <div className="bg-[#0F1419] border border-gray-800 rounded-2xl p-6">
                            <h3 className="text-xl font-bold mb-6 flex items-center justify-between">
                                <div className="flex items-center">
                                    <Calendar className="mr-2 text-green-500" size={24} />
                                    Storico Trade
                                </div>
                                <div className="flex items-center gap-3">
                                    <select
                                        value={tradeLimit}
                                        onChange={(e) => setTradeLimit(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                                        className="text-sm bg-gray-800 text-gray-300 border border-gray-700 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500 transition-colors"
                                    >
                                        <option value="10">10 trade</option>
                                        <option value="20">20 trade</option>
                                        <option value="50">50 trade</option>
                                        <option value="100">100 trade</option>
                                        <option value="all">Tutti</option>
                                    </select>
                                    <span className="text-sm font-normal text-gray-400 bg-gray-800/50 px-3 py-1 rounded-full">
                                        {data?.recentTrades.filter(t => {
                                            const action = String(t.action || '').toUpperCase();
                                            return action === 'BUY' || action === 'SELL';
                                        }).length} totali
                                    </span>
                                </div>
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-800">
                                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Apertura</th>
                                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Chiusura</th>
                                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Simbolo</th>
                                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Tipo</th>
                                            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-400">Pips</th>
                                            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-400">Profitto</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.recentTrades
                                            .filter(trade => {
                                                // Mostra solo trade chiusi (Buy/Sell), nasconde Limit, Delete, Deposit, ecc.
                                                const action = String(trade.action || '').toUpperCase();
                                                return action === 'BUY' || action === 'SELL';
                                            })
                                            .slice(0, tradeLimit === 'all' ? undefined : tradeLimit)
                                            .map((trade, index) => (
                                                <tr key={index} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                                                    <td className="py-3 px-4 text-sm text-gray-300">
                                                        {new Date(trade.openTime).toLocaleString('it-IT', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-gray-300">
                                                        {new Date(trade.closeTime).toLocaleString('it-IT', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </td>
                                                    <td className="py-3 px-4 text-sm font-semibold text-white">{trade.symbol}</td>
                                                    <td className="py-3 px-4">
                                                        <span className={`text-xs font-semibold px-2 py-1 rounded ${String(trade.action).toUpperCase() === 'BUY'
                                                                ? 'bg-green-500/20 text-green-400'
                                                                : 'bg-red-500/20 text-red-400'
                                                            }`}>
                                                            {trade.action}
                                                        </span>
                                                    </td>
                                                    <td className={`py-3 px-4 text-right font-bold ${trade.pips > 0 ? 'text-green-400' : 'text-red-400'
                                                        }`}>
                                                        {trade.pips > 0 ? '+' : ''}{trade.pips.toFixed(1)}
                                                    </td>
                                                    <td className={`py-3 px-4 text-right font-bold ${trade.profit > 0 ? 'text-green-400' : 'text-red-400'
                                                        }`}>
                                                        ${trade.profit > 0 ? '+' : ''}{trade.profit.toFixed(2)}
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </AnimatedSection>

                    {/* Info Banner */}
                    <AnimatedSection delay={800}>
                        <div className="bg-gradient-to-r from-blue-900/10 to-transparent border border-blue-500/20 rounded-2xl p-6 mt-8">
                            <p className="text-sm text-gray-400 leading-relaxed">
                                <strong className="text-blue-400">Nota:</strong> I dati mostrati sono sincronizzati in tempo reale con MyFXBook.
                                La dashboard si aggiorna automaticamente ogni ora. Per informazioni più dettagliate,
                                visita il <a href={EXTERNAL_LINKS.MYFXBOOK_BLUE_STRIKE} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">profilo MyFXBook ufficiale</a>.
                            </p>
                        </div>
                    </AnimatedSection>
                </div>
            </section>
        </div>
    );
};
